import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { thirteenWeekCashFlowApi } from "../../helpers/thirteenWeekCashFlowApi";

const request =
  (fn) =>
  async (args, { rejectWithValue }) => {
    try {
      return await fn(args);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || error?.message || "Request failed",
      );
    }
  };

export const getThirteenWeekSummary = createAsyncThunk(
  "thirteenWeekCashFlow/getSummary",
  request((params) => thirteenWeekCashFlowApi.getSummary(params)),
);
export const getThirteenWeekHistory = createAsyncThunk(
  "thirteenWeekCashFlow/getHistory",
  request(() => thirteenWeekCashFlowApi.getHistory()),
);
export const getThirteenWeekSettings = createAsyncThunk(
  "thirteenWeekCashFlow/getSettings",
  request(() => thirteenWeekCashFlowApi.getSettings()),
);
export const getThirteenWeekCards = createAsyncThunk(
  "thirteenWeekCashFlow/getCards",
  request(() => thirteenWeekCashFlowApi.getCards()),
);
export const getThirteenWeekSchedules = createAsyncThunk(
  "thirteenWeekCashFlow/getSchedules",
  request(() => thirteenWeekCashFlowApi.getSchedules()),
);
export const getThirteenWeekTransactions = createAsyncThunk(
  "thirteenWeekCashFlow/getTransactions",
  request((params) => thirteenWeekCashFlowApi.getTransactions(params)),
);
export const getThirteenWeekRevenuePlan = createAsyncThunk(
  "thirteenWeekCashFlow/getRevenuePlan",
  request(() => thirteenWeekCashFlowApi.getRevenuePlan()),
);
export const getThirteenWeekDisbursements = createAsyncThunk(
  "thirteenWeekCashFlow/getDisbursements",
  request((params) => thirteenWeekCashFlowApi.getDisbursements(params)),
);
export const getThirteenWeekDisbursementHistory = createAsyncThunk(
  "thirteenWeekCashFlow/getDisbursementHistory",
  request(() => thirteenWeekCashFlowApi.getDisbursementHistory()),
);
export const importThirteenWeekData = createAsyncThunk(
  "thirteenWeekCashFlow/importData",
  request(({ kind, csv, filename }) =>
    thirteenWeekCashFlowApi.importData(kind, csv, filename),
  ),
);
export const getThirteenWeekRules = createAsyncThunk(
  "thirteenWeekCashFlow/getRules",
  request(() => thirteenWeekCashFlowApi.getCategoryRules()),
);
export const getThirteenWeekHolidays = createAsyncThunk(
  "thirteenWeekCashFlow/getHolidays",
  request(() => thirteenWeekCashFlowApi.getHolidays()),
);

const initialState = {
  summary: null,
  settings: null,
  schedules: [],
  transactions: null,
  revenuePlan: null,
  disbursements: null,
  history: null,
  disbursementHistory: null,
  rules: [],
  builtinMappings: [],
  holidays: [],
  cards: [],
  loading: {},
  errors: {},
};

const thirteenWeekCashFlowSlice = createSlice({
  name: "thirteenWeekCashFlow",
  initialState,
  reducers: {
    optimisticOverride(state, action) {
      if (!state.summary) return;
      const { date, category, amount, note } = action.payload;
      const row = state.summary.daily?.find((item) => item.date === date);
      if (row) {
        row.values[category] = Number(amount);
        row.override = true;
        row.overrides = {
          ...(row.overrides || {}),
          [category]: { id: `${date}:${category}`, note: note || null },
        };
        row.overrideNote = note || null;
      }
    },
  },
  extraReducers: (builder) => {
    const handlers = [
      [getThirteenWeekSummary, "summary"],
      [getThirteenWeekHistory, "history"],
      [getThirteenWeekSettings, "settings"],
      [getThirteenWeekCards, "cards"],
      [getThirteenWeekSchedules, "schedules"],
      [getThirteenWeekTransactions, "transactions"],
      [getThirteenWeekRevenuePlan, "revenuePlan"],
      [getThirteenWeekDisbursements, "disbursements"],
      [getThirteenWeekDisbursementHistory, "disbursementHistory"],
      [getThirteenWeekHolidays, "holidays"],
    ];
    handlers.forEach(([thunk, key]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading[key] = true;
          state.errors[key] = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading[key] = false;
          state[key] =
            key === "transactions" || key === "disbursements" || key === "disbursementHistory"
              ? action.payload
              : (action.payload?.data ?? action.payload);
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading[key] = false;
          const canceled =
            axios.isCancel(action.error) ||
            action.error?.message === "canceled" ||
            action.payload === "canceled" ||
            action.payload?.message === "canceled";
          if (canceled) return;
          state.errors[key] =
            action.payload || action.error?.message || "Request failed";
        });
    });
    builder
      .addCase(getThirteenWeekRules.pending, (state) => {
        state.loading.rules = true;
        state.errors.rules = null;
      })
      .addCase(getThirteenWeekRules.fulfilled, (state, action) => {
        state.loading.rules = false;
        state.rules = action.payload?.data || [];
        state.builtinMappings = action.payload?.builtin || [];
      })
      .addCase(getThirteenWeekRules.rejected, (state, action) => {
        state.loading.rules = false;
        state.errors.rules =
          action.payload || action.error?.message || "Request failed";
      });
  },
});

export const { optimisticOverride } = thirteenWeekCashFlowSlice.actions;
export default thirteenWeekCashFlowSlice.reducer;
